import React, { useEffect, useState, useCallback } from 'react';
import classnames from 'classnames';
import { ErrorMessage, Field } from '@atlaskit/form';
import styled from '@emotion/styled';
import isEmail from 'validator/lib/isEmail';
import Textfield from '@atlaskit/textfield';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import DeleteIcon from '@atlaskit/icon/core/delete';
import { IconButton } from '@atlaskit/button/new';
import './add-customer-email-field.scss';

type EmailList = Map<string, User>;

const StyledHelperMessageLabel = styled.label({
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '16px',
  color: '#626F86',
});

const StyledInputDiv = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
});

interface Props {
  className?: string;
  emailList?: EmailList;
  setEmailList?: React.Dispatch<React.SetStateAction<EmailList>>;
  hideHelperText?: boolean;
  showAddContactAsButton?: boolean;
  showRemoveAsIcon?: boolean;
  showTextFieldLabel?: boolean;
}

const EMAIL_FIELD_TEXT = 'Enter email address';
const ADD_BUTTON_TEXT = 'Add customer contact';
const EMAIL_HELP_FIELD_TEXT = 'Email must be linked to the subscription or site';

interface User {
  email: string;
  error?: string | undefined;
}

export const AddCustomerEmailField = ({
  className,
  emailList,
  setEmailList,
  hideHelperText = false,
  showAddContactAsButton = false,
  showRemoveAsIcon = false,
  showTextFieldLabel = false,
}: Props) => {
  const [localEmailList, setLocalEmailList] = useState<Map<string, User>>(new Map());

  if (!emailList || !setEmailList) {
    emailList = localEmailList;
    setEmailList = setLocalEmailList;
  }

  useEffect(() => {
    if (emailList?.size === 0) {
      addEmailField();
    }
  }, [emailList?.size]);

  const handleEmailChange = (id: string, value: string) => {
    const emailData = emailList?.get(id) as User;
    emailData['email'] = value;
    emailList?.set(id, emailData);
    setEmailList?.(new Map(emailList));
  };

  // const handleEmailChangeAsync = useMemo(() => debounce(handleEmailChange, 300), []);

  const validateEmail = (value: string | undefined) => {
    if (!value) {
      return 'Required field';
    }
    if (!isEmail(value)) {
      return 'Enter a valid email address';
    }
  };

  const validateEmailAsync = useCallback(
    async (email: string | undefined) =>
      new Promise((resolve) => setTimeout(resolve, 600)).then(() => validateEmail(email?.trim())),
    [validateEmail]
  );

  const removeEmail = (id: string) => {
    emailList?.delete(id);
    setEmailList?.(new Map(emailList));
  };

  const addEmailField = () => {
    const id = +Date.now();
    const emailData = { email: '' };
    emailList?.set(id.toString(), emailData);
    setEmailList?.(new Map(emailList));
  };

  const getEmailField = (id: string, user: User) => {
    return (
      <div className="cart-email-item" key={id}>
        <Field
          name={`email-${id}`}
          label={showTextFieldLabel && 'Enter contact'}
          isRequired
          validate={validateEmailAsync}
        >
          {({ fieldProps, error }) => (
            <>
              <StyledInputDiv className="flex align-center">
                <Textfield
                  {...fieldProps}
                  className="cart-email-input"
                  testId={`customer-contact-email-${id}`}
                  autoComplete="off"
                  placeholder={EMAIL_FIELD_TEXT}
                  value={user.email}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleEmailChange(id, event.target.value.trim());
                    fieldProps.onChange(event);
                  }}
                />
                {/* @ts-expect-error TODO: after 3.0 rollout, mark emailList and setEmailList as mandatory and remove this expect error */}
                {emailList?.size > 1 ? (
                  showRemoveAsIcon ? (
                    <IconButton
                      label="remove-customer-contact"
                      icon={DeleteIcon}
                      onClick={() => removeEmail(id)}
                      testId="remove-customer-contact-btn"
                    />
                  ) : (
                    <Button
                      appearance="link"
                      testId={`remove-button-${id}`}
                      onClick={() => removeEmail(id)}
                      className="remove-button"
                    >
                      Remove
                    </Button>
                  )
                ) : null}
              </StyledInputDiv>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              {!hideHelperText && <StyledHelperMessageLabel>{EMAIL_HELP_FIELD_TEXT}</StyledHelperMessageLabel>}
            </>
          )}
        </Field>
      </div>
    );
  };

  const renderEmailList = () => {
    const emailFieldArr = [];
    // @ts-expect-error TODO: after 3.0 rollout, mark emailList and setEmailList as mandatory and remove this expect error
    for (const [id, value] of emailList?.entries?.()) {
      emailFieldArr.push(getEmailField(id, value));
    }
    return emailFieldArr;
  };

  return (
    <div className={classnames('cart-add-customer-email-container', className)}>
      <>
        <div className="cart-transfer-email-list">{renderEmailList()}</div>
        <div className="cart-transfer-add-email mt-8">
          <Button
            appearance={showAddContactAsButton ? 'default' : 'link'}
            testId="add-new-customer-contact"
            iconBefore={<AddIcon label="Add icon" size="small" />}
            onClick={addEmailField}
          >
            {ADD_BUTTON_TEXT}
          </Button>
        </div>
      </>
    </div>
  );
};
